import { useState, useCallback, useRef } from 'react';

export interface PlaceSuggestion {
  place_id: string;
  description: string;
  structured_formatting: {
    main_text: string;
    secondary_text: string;
  };
}

interface UsePlacesAutocompleteOptions {
  debounce?: number;
  requestOptions?: {
    componentRestrictions?: { country: string };
    types?: string[];
  };
}

interface UsePlacesAutocompleteReturn {
  value: string;
  setValue: (value: string) => void;
  suggestions: {
    status: 'OK' | 'ZERO_RESULTS' | 'ERROR' | 'IDLE';
    data: PlaceSuggestion[];
  };
}

export const usePlacesAutocomplete = (
  options: UsePlacesAutocompleteOptions = {}
): UsePlacesAutocompleteReturn => {
  const { debounce = 300, requestOptions = {} } = options;
  const [value, setValue] = useState('');
  const [suggestions, setSuggestions] = useState<{
    status: 'OK' | 'ZERO_RESULTS' | 'ERROR' | 'IDLE';
    data: PlaceSuggestion[];
  }>({
    status: 'IDLE',
    data: [],
  });

  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  const fetchSuggestions = useCallback(
    async (input: string) => {
      if (!input.trim() || input.trim().length < 3) {
        setSuggestions({ status: 'IDLE', data: [] });
        return;
      }

      const apiKey = process.env.REACT_APP_MAP_API_KEY;
      if (!apiKey) {
        setSuggestions({ status: 'IDLE', data: [] });
        return;
      }

      try {
        const body: any = {
          input,
          languageCode: 'fr',
        };

        if (requestOptions.componentRestrictions?.country) {
          body.includedRegionCodes = [
            requestOptions.componentRestrictions.country.toUpperCase(),
          ];
        }

        const response = await fetch(
          'https://places.googleapis.com/v1/places:autocomplete',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Goog-Api-Key': apiKey,
              'X-Goog-FieldMask': 'suggestions.placePrediction.placeId,suggestions.placePrediction.text,suggestions.placePrediction.structuredFormat',
            },
            body: JSON.stringify(body),
          }
        );

        if (!response.ok) {
          setSuggestions({ status: 'ZERO_RESULTS', data: [] });
          return;
        }

        const data = await response.json();

        if (data.suggestions && data.suggestions.length > 0) {
          // Transform the new API format to match the old format
          const transformedSuggestions: PlaceSuggestion[] = data.suggestions
            .map((s: any) => {
              const placePrediction = s.placePrediction;
              if (!placePrediction) return undefined;

              const placeId = placePrediction.placeId;
              const main =
                placePrediction.structuredFormat?.mainText?.text || '';
              const secondary =
                placePrediction.structuredFormat?.secondaryText?.text || '';
              const fullText =
                placePrediction.text?.text ||
                [main, secondary].filter(Boolean).join(', ');

              if (!placeId || !fullText) return undefined;

              return {
                place_id: placeId,
                description: fullText,
                structured_formatting: {
                  main_text: main,
                  secondary_text: secondary,
                },
              };
            })
            .filter(Boolean) as PlaceSuggestion[];

          setSuggestions({
            status: 'OK',
            data: transformedSuggestions,
          });
        } else {
          setSuggestions({
            status: 'ZERO_RESULTS',
            data: [],
          });
        }
      } catch (error) {
        console.error('Error fetching place suggestions:', error);
        setSuggestions({
          status: 'ERROR',
          data: [],
        });
      }
    },
    [requestOptions]
  );

  const handleSetValue = useCallback(
    (newValue: string) => {
      setValue(newValue);

      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }

      debounceTimer.current = setTimeout(() => {
        fetchSuggestions(newValue);
      }, debounce);
    },
    [debounce, fetchSuggestions]
  );

  return {
    value,
    setValue: handleSetValue,
    suggestions,
  };
};
